import { useEffect, useState } from 'react';
import {
  Button,
  EmptyState,
  ErrorState,
  Footnote,
  FormGroup,
  Group,
  Notice,
  Row,
  SectionLabel,
  Skeleton,
  StatusPill,
} from '@store/ui';
import { haptic } from '../../telegram';
import { useAsync } from '../shared';
import {
  adminCategories,
  createCategory,
  newIdempotencyKey,
  patchCategory,
  type AdminCategory,
  type CategoryWrite,
} from './client';
import { ActionBar, AdminHeader, AreaRow, TextRow, ToggleRow, useSubmit } from './fields';

// --- Категории ---------------------------------------------------------------

/**
 * The categories the home screen is built out of.
 *
 * Icon, picture and order are not decoration here: the storefront grid *is*
 * these rows, sorted by `sortOrder` and drawn from `imageUrl` with the emoji —
 * and then a monogram — behind it. Editing them anywhere else means editing the
 * shop's front page from a laptop, which is the thing this section exists to
 * stop being necessary.
 */

const HTTPS = /^https:\/\/\S+$/i;

export function AdminCategoriesScreen({
  onNavigate,
}: {
  readonly onNavigate: (route: string) => void;
}) {
  const list = useAsync(() => adminCategories(), []);
  const categories = list.data?.categories ?? [];

  if (list.error) {
    return (
      <div className="container stack">
        <AdminHeader title="Категории" />
        <ErrorState title="Категории не загрузились" text={list.error} onRetry={list.reload} />
      </div>
    );
  }

  return (
    <div className="container stack">
      <AdminHeader
        title="Категории"
        subtitle={list.loading ? null : `${categories.length} на главной`}
      />

      {list.loading ? (
        <Skeleton height={180} />
      ) : categories.length === 0 ? (
        <EmptyState
          title="Категорий пока нет"
          text="Главная собирается из категорий — без них покупателю некуда нажать."
        />
      ) : (
        <Group>
          {categories.map((category) => (
            <Row
              key={category.id}
              title={`${category.iconEmoji ? `${category.iconEmoji} ` : ''}${category.title}`}
              subtitle={`Порядок ${category.sortOrder}${category.parentId ? ' · вложенная' : ''}`}
              trailing={
                category.isPublished ? (
                  <StatusPill tone="success">В магазине</StatusPill>
                ) : (
                  <StatusPill tone="warning">Скрыта</StatusPill>
                )
              }
              chevron
              onClick={() => onNavigate(`/admin/categories/${category.id}`)}
            />
          ))}
        </Group>
      )}

      <Footnote>Меньший порядок — левее и выше на главной.</Footnote>

      <div className="action-bar">
        <Button block onClick={() => onNavigate('/admin/categories/new')}>
          Добавить категорию
        </Button>
      </div>
    </div>
  );
}

interface CategoryForm {
  title: string;
  description: string;
  iconEmoji: string;
  imageUrl: string;
  sortOrder: string;
  parentId: string | null;
  isPublished: boolean;
}

const blank = (): CategoryForm => ({
  title: '',
  description: '',
  iconEmoji: '',
  imageUrl: '',
  sortOrder: '0',
  parentId: null,
  isPublished: true,
});

const fromRow = (row: AdminCategory): CategoryForm => ({
  title: row.title,
  description: row.description ?? '',
  iconEmoji: row.iconEmoji ?? '',
  imageUrl: row.imageUrl ?? '',
  sortOrder: String(row.sortOrder),
  parentId: row.parentId,
  isPublished: row.isPublished,
});

export function AdminCategoryFormScreen({
  categoryId,
  onNavigate,
}: {
  readonly categoryId: string | null;
  readonly onNavigate: (route: string) => void;
}) {
  const list = useAsync(() => adminCategories(), []);
  const [form, setForm] = useState<CategoryForm>(blank);
  const [loaded, setLoaded] = useState<CategoryForm | null>(null);
  const [done, setDone] = useState(false);
  const { busy, error, setError, run } = useSubmit();
  const idempotency = useState(() => newIdempotencyKey())[0];

  const categories = list.data?.categories ?? [];
  const existing = categoryId ? categories.find((row) => row.id === categoryId) : undefined;

  useEffect(() => {
    if (!existing) return;
    const next = fromRow(existing);
    setForm(next);
    setLoaded(next);
  }, [existing]);

  const set = <K extends keyof CategoryForm>(key: K, value: CategoryForm[K]) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const imageBad = form.imageUrl.trim() !== '' && !HTTPS.test(form.imageUrl.trim());
  const sortBad = !/^\d+$/.test(form.sortOrder.trim());
  const blockers: string[] = [];
  if (form.title.trim() === '') blockers.push('Название обязательно.');
  if (imageBad) blockers.push('Ссылка на картинку должна начинаться с https://.');
  if (sortBad) blockers.push('Порядок — целое число от 0.');

  const save = () =>
    run(async () => {
      /* Only what moved, for the same reason the product form does it: a PATCH
         that restates every field can un-hide a category nobody touched. */
      const body: CategoryWrite = {};
      const changed = (key: keyof CategoryForm) => loaded === null || loaded[key] !== form[key];

      if (changed('title')) body.title = form.title.trim();
      if (changed('description')) body.description = form.description.trim() || null;
      if (changed('iconEmoji')) body.iconEmoji = form.iconEmoji.trim() || null;
      if (changed('imageUrl')) body.imageUrl = form.imageUrl.trim() || null;
      if (changed('sortOrder')) body.sortOrder = Number(form.sortOrder.trim());
      if (changed('parentId')) body.parentId = form.parentId;
      if (changed('isPublished')) body.isPublished = form.isPublished;

      if (categoryId) {
        if (Object.keys(body).length > 0) await patchCategory(categoryId, body);
      } else {
        await createCategory(body, idempotency);
      }

      haptic.success();
      setDone(true);
      list.reload();
    });

  if (list.loading) {
    return (
      <div className="container stack">
        <Skeleton height={34} width="60%" />
        <Skeleton height={220} />
      </div>
    );
  }

  if (categoryId && !existing) {
    return (
      <div className="container stack">
        <AdminHeader title="Категория" />
        <ErrorState
          title="Категория не найдена"
          text={list.error ?? 'Возможно, её удалили с другого устройства.'}
          onRetry={list.reload}
        />
      </div>
    );
  }

  /* Only top-level categories may be a parent: the home screen walks one level,
     and a category nested two deep is a row nobody can reach. */
  const parents = categories.filter((row) => row.parentId === null && row.id !== categoryId);

  return (
    <div className="container stack">
      <AdminHeader
        title={categoryId ? 'Категория' : 'Новая категория'}
        subtitle={categoryId ? form.title || null : 'Появится на главной сразу после сохранения'}
      />

      <FormGroup>
        <TextRow
          label="Название"
          id="c-title"
          value={form.title}
          onChange={(value) => set('title', value)}
          placeholder="Подписки"
          maxLength={160}
          invalid={form.title.trim() === ''}
        />
        <TextRow
          label="Иконка"
          id="c-icon"
          value={form.iconEmoji}
          onChange={(value) => set('iconEmoji', value)}
          placeholder="🎧"
          maxLength={16}
        />
        <TextRow
          label="Картинка"
          id="c-image"
          value={form.imageUrl}
          onChange={(value) => set('imageUrl', value)}
          placeholder="https://…"
          inputMode="url"
          invalid={imageBad}
        />
        <TextRow
          label="Порядок"
          id="c-sort"
          value={form.sortOrder}
          onChange={(value) => set('sortOrder', value)}
          placeholder="0"
          inputMode="numeric"
          invalid={sortBad}
        />
        <AreaRow
          label="Описание"
          id="c-description"
          value={form.description}
          onChange={(value) => set('description', value)}
          placeholder="Необязательно"
          rows={3}
          maxLength={2000}
        />
      </FormGroup>
      <Footnote>
        Картинка — ссылка, которую вы вставляете: загрузки файлов в магазине пока нет. Без картинки
        на плитке покажутся первые буквы названия.
      </Footnote>

      <Group>
        <ToggleRow
          title="Показывать на главной"
          value={form.isPublished}
          onChange={(value) => set('isPublished', value)}
        />
      </Group>

      {parents.length > 0 ? (
        <section>
          <SectionLabel>Вложить в категорию</SectionLabel>
          <Group>
            <Row
              title="Верхний уровень"
              selected={form.parentId === null}
              onClick={() => {
                haptic.select();
                set('parentId', null);
              }}
            />
            {parents.map((parent) => (
              <Row
                key={parent.id}
                title={parent.title}
                selected={form.parentId === parent.id}
                onClick={() => {
                  haptic.select();
                  set('parentId', parent.id);
                }}
              />
            ))}
          </Group>
        </section>
      ) : null}

      {done ? (
        <Group>
          <Row
            icon="circleCheck"
            title="Сохранено"
            subtitle="К списку категорий"
            chevron
            onClick={() => onNavigate('/admin/categories')}
          />
        </Group>
      ) : null}

      <ActionBar error={error}>
        {blockers.length > 0 ? (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Notice tone="warning">{blockers.join(' ')}</Notice>
          </div>
        ) : null}
        <Button
          block
          loading={busy}
          disabled={blockers.length > 0}
          onClick={() => {
            setDone(false);
            setError(null);
            void save();
          }}
        >
          {categoryId ? 'Сохранить' : 'Создать категорию'}
        </Button>
      </ActionBar>
    </div>
  );
}
